// Formül / HESAPLANAMADI popover'ı (M16 T10, kural 6/13): aktif varyant etiketi + ifade, girdi
// değerleri (aynı kolon), neden satırı (girdi bekleniyor / payda sıfır), eksik girdiler tıklanabilir
// (hücreye scroll + vurgu + kategori aç — KpiGrid `onJumpTo`). Portal (P3-18): `labelByCode`
// verilirse ifadedeki kodlar gösterge ETİKETİYLE yazılır (kod `title`'da). Müşteriye KAPALI girdi
// (Dalga 3, sızıntı yok) ne ham kod ne humanize edilmiş ad gösterir: ifadede ve girdi listesinde
// `KPI_COPY.portal.notes.hiddenInput` ("erişiminiz olmayan gösterge"). Danışman görünümü byte-eşit.
// K8 (Finar): "Ölçülmüyor" atlanan kalemler (`cell.partialRefs`) ayrı blokta listelenir (portalda
// kapalı kod → `hiddenInput`); tüm kalemleri ölçülmeyen hesap (`allUnmeasured`) gerekçesi ayrıdır.
// Kırılım (spec §6.2, DR4): `breakdown` verilirse formülsüz toplamda örtük `TOPLA_ALT()` + hesap
// grubu adı, hesap/doğrulama satırları ve dört durum KpiCellBreakdownSection'da; genel "neden" satırı
// ve "Raporlanan değer gir…" satırı o zaman bölüme bırakılır (tekrar yok).
// TODO-KPI-91: AGIRLIKLI_ORT hücresinde sırayla eşlenen çiftler değerleriyle (`wavgPairs`); hücre
// `failure === "wavg_count_mismatch"` → gerekçe "alt kalem sayıları eşit değil". Çiftler yalnız
// danışman görünümünde verilir (portalda kapalı kalem grid'de yok — sıra yanıltıcı olurdu).

import { useRef } from "react";
import { createPortal } from "react-dom";
import type { KpiResolvedCell, KpiResolvedIndicator } from "@mahir/shared";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import { maskedFormulaTokens } from "../../lib/kpi-formula-hover";
import { partialInfoFor, type PartialLookup } from "../../lib/kpi-partial-info";
import { formatKpiCell, KPI_COPY } from "../../lib/kpi-ui";
import { type WavgPopoverPair } from "../../lib/kpi-wavg-pairs";
import { KpiCellBreakdownSection, type KpiCellBreakdownProps } from "./KpiCellBreakdownSection";
import { placementStyle, usePopoverPlacement } from "./use-popover-placement";

export interface FormulaPopoverProps {
  indicator: KpiResolvedIndicator;
  cell: KpiResolvedCell | undefined;
  columnLabel: string;
  /** çapa hücre (viewport konumu her ölçümde okunur — kaydırmada popover takip eder) */
  getAnchor: () => HTMLElement | null;
  /** aynı kolondaki girdi hücreleri: kod → {indicator, cell} */
  inputs: { code: string; label: string; cell: KpiResolvedCell | undefined; indicatorId: string }[];
  onJumpTo: (indicatorId: string) => void;
  onClose: () => void;
  /** T19/DR11 — portalda grid'de olmayan (closed) girdi kodları: "danışman tarafından girilir" */
  hiddenInputs?: string[] | undefined;
  /** P3-18 — kod → etiket; verilirse ifade etiketlerle yazılır (portal) */
  labelByCode?: Record<string, string> | undefined;
  /**
   * Danışman P2-12 — hücre raporlanan/sabit değer taşıyorsa "Sabiti kaldır / Raporlanan değer…"
   * düğmesi ReportedPopover'ı açar (sabit çözme yolu orada). Portalda verilmez.
   */
  onOpenReported?: (() => void) | undefined;
  /**
   * Danışman (2026-10-08 manuel test 8.1): hücre henüz raporlanan değer taşımıyorsa "Raporlanan
   * değer gir…" → hücre editörünün Kanıt sekmesi (girişi alanı orada). Portalda verilmez.
   */
  onEnterReported?: (() => void) | undefined;
  /** Dalga 3 (spec §6) — türetilmiş görünür yaprak hücrede de iş parçacığı başlatılır: "Not ekle" */
  onOpenNotes?: (() => void) | undefined;
  /** K8 — "Ölçülmeyen" bloğu için kod → etiket (grid geneli; atlanan kod torun olabilir) */
  partialLookup?: PartialLookup | undefined;
  /** TODO-KPI-91 — AGIRLIKLI_ORT çiftleri (aynı kolon, sırayla; `wavgPopoverPairs`) */
  wavgPairs?: WavgPopoverPair[][] | undefined;
  /** Kırılım bölümü (türetilmiş / ham toplam; `breakdownEditorInfo`) — yoksa davranış byte-eşit */
  breakdown?: KpiCellBreakdownProps | undefined;
}

export function FormulaPopover(props: FormulaPopoverProps): JSX.Element {
  const { indicator, cell, columnLabel, getAnchor, inputs, onJumpTo, onClose } = props;
  const hidden = props.hiddenInputs ?? [];
  const labelByCode = props.labelByCode;
  // Dalga 2 K10 — müşteri + formüllü gösterge: formül salt-okunur, varyant talebi yok
  const clientDerived = !!labelByCode && indicator.isDerived;
  // kapalı girdi kodu etiket/ipucu olarak ASLA görünmez (kod kapalı göstergenin kimliğidir) —
  // maske hover kartıyla tek kaynak: lib/kpi-formula-hover.ts `maskedFormulaTokens`
  const active =
    indicator.formulas.find((f) => f.id === indicator.activeFormulaId) ??
    indicator.formulas.find((f) => f.isActive);
  const missing = new Set(cell?.missingRefs ?? []);
  const copy = labelByCode ? KPI_COPY.portal : KPI_COPY.cell;
  const bd = props.breakdown;
  const implicit = !active && bd?.info.implicitFormula ? bd.info.implicitFormula : null;
  // kırılım durumu varsa gerekçe bölümde (dört durum) — genel "neden" satırı tekrarlanmaz
  const reason =
    cell?.status === "calc_error" && !bd?.info.state
      ? cell.hasPending
        ? "bir girdi bekleniyor (XXX)"
        : cell.failure === "wavg_count_mismatch"
          ? copy.wavgMismatchReason
          : cell.failure === "empty_group"
            ? copy.wavgEmptyReason
            : cell.allUnmeasured === true
              ? copy.allUnmeasuredReason
              : missing.size > 0
                ? "girdi eksik"
                : "payda sıfır ya da girdi hesaplanamadı"
      : null;
  // K8 — atlanan (Ölçülmüyor) kalemler: etiket grid'den (yoksa girdi listesinden); kapalı → hiddenInput
  const skipped = partialInfoFor(
    cell,
    props.partialLookup ?? {
      labelByCode: new Map(inputs.map((i) => [i.code, i.label])),
      nameByEntity: new Map(),
    },
    { client: !!labelByCode, hiddenInputs: hidden },
  ).refs;
  // P3-17: viewport altına taşarsa hücrenin üstüne açılır; portal + fixed (2026-10-07) — tablo
  // kabının overflow'u kırpamaz
  const rootRef = useRef<HTMLDivElement>(null);
  const pos = usePopoverPlacement(rootRef, getAnchor, []);

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label={`Formül: ${indicator.label}`}
      className="rounded-xl p-4 text-sm"
      data-placement={pos.placement}
      style={{
        ...placementStyle(pos),
        width: 360,
        fontFamily: "var(--font-sans)",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-md, 0 12px 32px rgba(15,42,82,.18))",
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="font-semibold flex-1" style={{ color: "var(--color-text)" }}>
          {indicator.label} · {columnLabel}
        </div>
        <button
          type="button"
          aria-label="Kapat"
          onClick={onClose}
          style={{ color: "var(--color-text-3)", minWidth: 28, minHeight: 28 }}
        >
          ✕
        </button>
      </div>
      {active ? (
        <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
          <b>{KPI_COPY.portal.formulaLabel}</b> ({active.label}):{" "}
          {labelByCode ? (
            <span data-testid="kpi-formula-expression" style={{ color: "var(--color-text)" }}>
              {maskedFormulaTokens(active.expression, labelByCode, hidden).map((t, i) =>
                t.ref ? (
                  <span
                    key={i}
                    {...(t.code !== undefined ? { title: t.code } : {})}
                    style={{ fontWeight: 600 }}
                  >
                    {t.text}
                  </span>
                ) : (
                  <span key={i}>{t.text}</span>
                ),
              )}
            </span>
          ) : (
            <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}>
              {active.expression}
            </code>
          )}
        </div>
      ) : implicit ? (
        <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
          <b>{KPI_COPY.portal.formulaLabel}</b>
          {bd?.info.calc ? ` (${bd.info.calc.groupLabel})` : ""}:{" "}
          <code
            data-testid="kpi-formula-implicit"
            style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}
          >
            {implicit}
          </code>
        </div>
      ) : (
        <div className="text-xs" style={{ color: "var(--color-warning-text)" }}>
          {labelByCode
            ? KPI_COPY.portal.noActiveFormula
            : "Aktif formül yok — veri seti editöründen bir varyant seçin."}
        </div>
      )}
      {reason ? (
        <div className="text-xs mt-2" style={{ color: "var(--color-warning-text)" }}>
          <b>Neden hesaplanamadı:</b> {reason}
        </div>
      ) : cell?.status === "filled" ? (
        <div className="text-xs mt-2" style={{ color: "var(--color-text-2)" }}>
          <b>Sonuç:</b> {formatKpiCell(cell, { percent: indicator.valueType === "percent" }).text}
          {cell.isReported ? (
            <span className="ml-2" style={{ color: "var(--color-primary)" }}>
              · raporlanan değer{cell.pinned ? " (sabit)" : ""}
            </span>
          ) : null}
          {cell.isReported && props.onOpenReported ? (
            // P2-12: sabitlenmiş hücreye tıklayınca bu popover açılıyordu ve sabiti çözme yolu yoktu
            <button
              type="button"
              className="ml-2"
              data-testid="kpi-formula-open-reported"
              onClick={props.onOpenReported}
              style={{ color: "var(--color-primary)", textDecoration: "underline", minHeight: 24 }}
            >
              {cell.pinned
                ? `${KPI_EVIDENCE_COPY.reported.unpin}…`
                : `${KPI_EVIDENCE_COPY.reported.title}…`}
            </button>
          ) : null}
          {typeof cell.reportedMismatch === "number" ? (
            <span className="ml-2" style={{ color: "var(--color-warning-text)" }}>
              · raporlanan değerden fark (hesap − raporlanan):{" "}
              {cell.reportedMismatch.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
            </span>
          ) : null}
        </div>
      ) : null}
      {bd ? (
        <KpiCellBreakdownSection {...bd} client={!!labelByCode} columnLabel={columnLabel} />
      ) : null}
      {props.onEnterReported && !(bd?.info.role === "derived_total" && !cell?.isReported) ? (
        <div className="text-xs mt-2">
          <button
            type="button"
            data-testid="kpi-formula-enter-reported"
            onClick={props.onEnterReported}
            style={{ color: "var(--color-primary)", textDecoration: "underline", minHeight: 24 }}
          >
            {cell?.isReported
              ? KPI_EVIDENCE_COPY.reported.change
              : KPI_EVIDENCE_COPY.reported.enter}
          </button>
          {!cell?.isReported ? (
            <span style={{ color: "var(--color-text-3)" }}>
              {" "}
              — raporda yayımlanan değeri yazın; hesap varsa fark rozette görünür, sabitlerseniz
              hesabın yerine geçer.
            </span>
          ) : null}
        </div>
      ) : null}
      {inputs.length > 0 ? (
        <ul
          className="text-xs mt-2"
          style={{ margin: "8px 0 0 14px", lineHeight: 1.8, color: "var(--color-text-2)" }}
        >
          {inputs.map((i) => {
            const d = formatKpiCell(i.cell);
            const bad = missing.has(i.code) || i.cell?.status !== "filled";
            return (
              <li key={i.code}>
                {bad ? (
                  <button
                    type="button"
                    onClick={() => onJumpTo(i.indicatorId)}
                    style={{
                      color: "var(--color-primary)",
                      textDecoration: "underline",
                      minHeight: 24,
                    }}
                  >
                    {i.label} — {d.text} → hücreye git
                  </button>
                ) : (
                  <span>
                    {i.label} — {d.text} ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {(props.wavgPairs ?? []).map((group, gi) => (
        <div key={gi} className="text-xs mt-2" data-testid="kpi-formula-wavg-pairs">
          <b style={{ color: "var(--color-text-2)" }}>{KPI_COPY.cell.wavgPairsTitle}</b>
          <ul style={{ margin: "4px 0 0 14px", lineHeight: 1.8, color: "var(--color-text-2)" }}>
            {group.map((p, i) => (
              <li key={i}>
                {p.valueLabel} ({formatKpiCell(p.valueCell).text}) ↔ {p.weightLabel} (
                {formatKpiCell(p.weightCell).text})
              </li>
            ))}
          </ul>
        </div>
      ))}
      {skipped.length > 0 ? (
        <div className="text-xs mt-2" data-testid="kpi-formula-partial-refs">
          <b style={{ color: "var(--color-text-2)" }}>{copy.partialRefs}</b>
          <ul style={{ margin: "4px 0 0 14px", lineHeight: 1.8, color: "var(--color-text-2)" }}>
            {skipped.map((r) => (
              <li key={r.code} {...(r.hidden || labelByCode ? {} : { title: r.code })}>
                {r.hidden ? KPI_COPY.portal.notes.hiddenInput : r.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hidden.length > 0 ? (
        <ul
          className="text-xs"
          style={{ margin: "4px 0 0 14px", lineHeight: 1.8, color: "var(--color-text-3)" }}
          data-testid="kpi-formula-hidden-inputs"
        >
          {hidden.map((code) => (
            <li key={code} {...(labelByCode ? {} : { title: code })}>
              {labelByCode ? KPI_COPY.portal.notes.hiddenInput : code} —{" "}
              {KPI_COPY.portal.derivedClosedInput}
            </li>
          ))}
        </ul>
      ) : null}
      {cell?.unverified ? (
        <div className="text-xs mt-2" style={{ color: "var(--color-warning-text)" }}>
          Girdilerden biri {KPI_COPY.workflow.submitted.toLocaleLowerCase("tr")} — rapora XXX gider.
        </div>
      ) : null}
      {clientDerived ? (
        <div
          className="text-xs mt-2"
          data-testid="kpi-formula-readonly"
          style={{ color: "var(--color-text-3)" }}
        >
          {KPI_COPY.portal.formulaReadonly}
        </div>
      ) : null}
      {props.onOpenNotes ? (
        <button
          type="button"
          data-testid="kpi-note-add"
          onClick={props.onOpenNotes}
          className="mt-3 text-xs underline"
          style={{ color: "var(--color-primary)", minHeight: 28 }}
        >
          {labelByCode ? KPI_COPY.portal.notes.addNote : KPI_COPY.notes.addNote}
        </button>
      ) : null}
    </div>,
    document.body,
  );
}
